/**
 * Yield Basis `LT` (yb-<ASSET>) + `AMM` (LEVAMM) read surface.
 *
 * `LT` is **ERC-4626-like but not strict** (docs.yieldbasis.com/dev/
 * integration/deposit-withdraw): it emits the 4626 `Deposit`/`Withdraw`
 * events and exposes `preview_deposit`/`preview_withdraw`, but there is
 * no `asset()`, `totalAssets()`, `convertToAssets()`, `convertToShares()`,
 * `mint()`, `redeem()`, `previewMint()` or `previewRedeem()` — it
 * implements `IERC20` and nothing else of the standard. The generic
 * `erc4626` reader would drop every entry, hence this ABI.
 *
 * Snake_case is not a typo — the contracts are Vyper 0.4.3 and the
 * selectors follow the source names verbatim. `pricePerShare` is the one
 * camelCase getter.
 *
 * `value_oracle()` returns the Vyper struct `OraclizedValue{p_o, value}`.
 * A static struct ABI-encodes identically to two flat return values, so
 * it is declared here as two `uint256` outputs — viem then hands back a
 * plain `[p_o, value]` array instead of an object, which is what the
 * reader consumes. `p_o` is the **Cryptoswap LP price**
 * (`2·virtual_price·√price_scale`), *not* the asset price — the asset
 * price the LT itself uses lives behind an internal `_price_oracle()`
 * with no public getter. The reader never needs it (see there).
 */

/** The `yb-<ASSET>` share token. `totalSupply()` comes from the shared ABI. */
export const YieldBasisLtReadAbi = [
  {
    /**
     * Fundamental, oracle-anchored value of one share — **1e18-scaled in
     * ASSET units regardless of the asset's own decimals**. cbBTC and
     * WBTC are 8-decimal while this still answers in 1e18, so treating it
     * as raw underlying is a 1e10 error. Sandwich-resistant; excludes
     * TRD. Returns exactly `1e18` when `totalSupply == 0`.
     */
    name: 'pricePerShare',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint256' }],
  },
  {
    /**
     * Realistic redemption: what `withdraw(tokens, …)` would actually pay,
     * in **raw underlying units**, priced off a live Cryptoswap unwind
     * (`calc_withdraw_fixed_out`). Includes TRD, so it disagrees with
     * `pricePerShare` by the signed discount — usually a few basis points,
     * widening during volatility.
     *
     * `@view @nonreentrant` in Vyper: the lock is only read, never
     * written, so this is a normal `eth_call` and multicall-safe.
     */
    name: 'preview_withdraw',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ type: 'uint256', name: 'tokens' }],
    outputs: [{ type: 'uint256', name: 'assets' }],
  },
] as const

/** The market's constant-leverage AMM — the deposit cap lives here. */
export const YieldBasisAmmReadAbi = [
  {
    /**
     * Ceiling on the market's crvUSD debt, set by the Factory's
     * allocation. The deposit assert is `max_debt() / 2 >= value`, so
     * `max_debt()/2` is the market's total equity capacity — there is no
     * `maxDeposit()` getter anywhere and a full market reverts
     * `"Debt too high"`.
     */
    name: 'max_debt',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint256' }],
  },
  {
    /**
     * `OraclizedValue{p_o, value}` flattened — see the file header.
     * `value` is the market's current **equity** in crvUSD (collateral
     * value minus debt, i.e. half the LP collateral at the L=2 target),
     * which is the quantity `max_debt()/2` is compared against.
     */
    name: 'value_oracle',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [
      { type: 'uint256', name: 'p_o' },
      { type: 'uint256', name: 'value' },
    ],
  },
] as const
